import { Component, OnInit } from '@angular/core';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { FormControl, FormGroup, ValidationErrors } from '@angular/forms';
import { Fields } from 'src/app/utils/fields';
import {
  VIPSeasonConstant,
  SeasonDates,
} from 'src/app/shared/models/constants/vipSeason';
import { DateConstants } from 'src/app/utils/date';
import { VipSeasonsDTO } from 'src/app/shared/models/dtos/responses/vip-seasons-dto/vip-seasons-dto';
import { SnackBarService } from 'src/app/core/services/snackbar/snackbar.service';
import { UserService } from 'src/app/core/api/user/user.service';
import { ActivatedRoute, Router } from '@angular/router';
import { VipLevelSettings } from 'src/app/shared/models/constants/vip-level-settings';
import { AuthenticatedUserService } from 'src/app/core/services/authenticated-user/authenticated-user.service';
import { map, Subscription } from 'rxjs';
import { RoutesKeys } from 'src/app/utils/routes';
import { DialogService } from 'src/app/core/services/dialog/dialog.service';
import { VipFormBuilderService } from 'src/app/core/services/vip-form-builder/vip-form-builder.service';
import { ProductService } from 'src/app/core/api/product/product.service';

@Component({
  selector: 'app-edit-vip-settings',
  templateUrl: './edit-vip-settings.component.html',
  styleUrls: ['./edit-vip-settings.component.scss'],
})
/**
 * Component for editing Vip Settings.
 */
export class EditVipSettingsComponent implements OnInit {
  labels = {
    saveChanges: 'save-changes',
    cancel: 'cancel',
    vipLevelName: 'vip-level-name',
    inkRequired: 'ink-required-points-to-level',
    discount: 'discount',
    editSeriesTitle: 'series-form-modal.title-edit-series',
    startsOn: 'starts-on',
    endsOn: 'ends-on',
    changesApplyAllRegions: 'changes-apply-all-regions',
    savingError: 'unexpected-error',
    success: 'success-updating-data',
    controlPanel: 'control-panel',
    addVipLevel: 'series-form-modal.add-vip-level',
    emptyList: 'series-form-modal.empty-list',
  };
  errorMsg = {
    requiredMsg: 'series-form-modal.error-messages.required',
    specialCharacterMsg: 'series-form-modal.error-messages.special-character',
    minLengthMsg: 'series-form-modal.error-messages.min-length',
    minInkLevelValueMsg: 'series-form-modal.error-messages.min-ink-level-value',
    maxInkLevelValueMsg: 'series-form-modal.error-messages.max-ink-level-value',
    minDiscountValueMsg: 'series-form-modal.error-messages.min-discount-value',
    maxDiscountValueMsg: 'series-form-modal.error-messages.max-discount-value',
  };
  formVipCurrent: FormGroup = new FormGroup({});
  formVipNext: FormGroup = new FormGroup({});
  vipLevels: VipLevelSettings[] = [];
  vipFields = [
    Fields.LABEL,
    Fields.BUSINESS_LEVEL_ID,
    Fields.NAME,
    Fields.LEVEL_POINTS,
    Fields.DISCOUNT,
  ];
  private currentRegionId: number | null = null;
  allRegions: number[] = [];
  seasons?: VipSeasonsDTO;
  VIPSeasonConstantObject = VIPSeasonConstant;
  allRegionsControl = false;
  loading = false;
  editEnabled = false;
  familyId?: number;
  familyDto: any;
  private readonly subsink = new Subscription();

  // prettier-ignore
  /**
   * constructor
   */
  constructor( // NOSONAR
    private readonly translationService: TranslationService,
    private readonly snackbarService: SnackBarService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly authenticatedUserService: AuthenticatedUserService,
    private readonly router: Router,
    private readonly dialogService: DialogService,
    private readonly vipFormBuilder: VipFormBuilderService,
    private readonly productService: ProductService,
    private readonly userService: UserService
  ) {}

  /**
   * On init
   */
  ngOnInit(): void {
    this.loading = true;
    this.translationService.translateObject(this.labels);
    this.translationService.translateObject(this.errorMsg);
    this.userService.getRegions().subscribe((regions) => {
      this.allRegions = regions.map((region) => region.id);
    });
    this.subsink.add(
      this.authenticatedUserService.currentRegion$.subscribe((region) => {
        if (!region) return;
        this.currentRegionId = region.id;
        this.editEnabled = false;
        this.retrieveVipConfigFromCurrentRegion();
      })
    );
    this.activatedRoute.params.subscribe((params) => {
      this.familyId = Number(params['id']);
      this.getFamily();
    });
  }

  /**
   * get family
   */
  getFamily() {
    this.productService
      .getAllFamilyProduct()
      .pipe(
        map((families) => families.find((item) => item.id === this.familyId))
      )
      .subscribe({
        next: (family) => {
          this.familyDto = family;
        },
      });
  }

  /**
   * Create Vip Settings Form
   */
  createVipSettingsForms(): void {
    this.formVipCurrent = this.vipFormBuilder.createVipForm(
      this.vipLevels,
      this.vipFields
    );
    this.formVipNext = this.vipFormBuilder.createVipForm(
      this.vipLevels,
      this.vipFields
    );
    this.loading = false;
  }

  /**
   * Save button click action
   */
  clickSave(): void {
    this.saveNewFamily();
  }

  /**
   * Cancel button click action
   */
  clickCancel(): void {
    this.router.navigate([RoutesKeys.CONTROL_PANEL]);
  }

  /**
   * retrieves the form control associated with the specified family and language.
   * @param {string} vipLevel
   * @param {string} field
   * @return {FormControl}
   */
  getCurrentControlByVipType(vipLevel: string, field: string): FormControl {
    return this.formVipCurrent.get([vipLevel, field].join('.')) as FormControl;
  }

  /**
   * retrieves the form control associated with the specified family and language.
   * @param {string} vipLevel
   * @param {string} field
   * @return {FormControl}
   */
  getNextControlByVipType(vipLevel: string, field: string): FormControl {
    return this.formVipNext.get([vipLevel, field].join('.')) as FormControl;
  }

  /**
   * call to the service and save a new family
   */
  saveNewFamily(): void {
    if (this.formVipNext.invalid) {
      return;
    }
    const regions = this.allRegionsControl
      ? this.allRegions
      : [this.currentRegionId].filter((id): id is number => id !== null);
    const formVipValues = this.formVipNext.getRawValue();
    const familyConfigs = Object.values(formVipValues);
    const data = {
      regionIds: regions,
      vipConfigs: familyConfigs,
    };
    this.loading = true;
    this.userService.updateVipRegion(data).subscribe({
      next: () => {
        this.snackbarService.success(this.labels.success);
        this.loading = false;
        this.editEnabled = false;
      },
      error: () => {
        this.snackbarService.error(this.labels.savingError);
        this.loading = false;
      },
    });
  }

  /**
   * Retrieves a VIP config from an specific region
   */
  retrieveVipConfigFromCurrentRegion() {
    if (!this.currentRegionId) return;
    this.userService.getVipRegion(this.currentRegionId).subscribe((seasons) => {
      this.seasons = seasons;
      this.defineFormSeasonData();
    });
  }

  /**
   * Defines current season data
   */
  defineFormSeasonData(): void {
    if (this.vipLevels.length > 0) {
      this.resetForm();
    }
    const currentSeasonConfigs = this.seasons?.current.filter(
      (seasonConfig) => seasonConfig.businessLevel.familyPk === this.familyId
    )!;
    const nextSeasonConfigs = this.seasons?.next.filter(
      (seasonConfig) => seasonConfig.businessLevel.familyPk === this.familyId
    )!;
    for (const config of nextSeasonConfigs) {
      this.vipLevels.push({
        label: 'Vip ' + (this.vipLevels.length + 1),
        id: String(config.businessLevel.id),
        markedForDeletion: config.businessLevel.markedForDeletion,
      });
    }
    this.createVipSettingsForms();
    let index = 0;
    this.vipLevels.forEach((vip) => {
      (this.formVipCurrent.get(vip.id) as FormGroup).patchValue({
        businessLevelId: currentSeasonConfigs[index].businessLevel.id,
        name: currentSeasonConfigs[index].name,
        levelPoints: currentSeasonConfigs[index].levelPoints,
        discount: currentSeasonConfigs[index].discount,
      });
      (this.formVipNext.get(vip.id) as FormGroup).patchValue({
        businessLevelId: nextSeasonConfigs[index].businessLevel.id,
        name: nextSeasonConfigs[index].name,
        levelPoints: nextSeasonConfigs[index].levelPoints,
        discount: nextSeasonConfigs[index].discount,
      });
      index++;
    });
  }

  /**
   * Enable the VIP edit for the current region
   */
  changeVIPEdit() {
    this.editEnabled = !this.editEnabled;
    if (!this.editEnabled) {
      this.defineFormSeasonData();
    }
  }

  /**
   * Define season dates
   * @param {string} season
   * @return {SeasonDates}
   */
  defineSeasonDates(season: string): SeasonDates {
    const today = new Date();
    const startingDateSeason2 = new Date(today.getFullYear(), 6, 1);

    const firstSemester = {
      startsOn: DateConstants.FIRST_SEMESTER_STARTS_ON,
      endsOn: DateConstants.FIRST_SEMESTER_ENDS_ON,
    };
    const secondSemester = {
      startsOn: DateConstants.SECOND_SEMESTER_STARTS_ON,
      endsOn: DateConstants.SECOND_SEMESTER_ENDS_ON,
    };

    const isSecondSemester = today > startingDateSeason2;

    if (season === VIPSeasonConstant.CURRENT) {
      return isSecondSemester ? secondSemester : firstSemester;
    } else {
      return isSecondSemester ? firstSemester : secondSemester;
    }
  }

  /**
   * Define generic errors
   * @return {string[]}
   */
  getGenericErrors(): string[] {
    const messages: string[] = [];
    Object.keys(this.formVipNext.controls).forEach((vipId) => {
      const group = this.formVipNext.get(vipId) as FormGroup;
      this.collectGroupErrors(group, messages);
    });
    return [...new Set(messages)];
  }

  /**
   * @param {FormGroup} group
   * @param {string[]} messages
   */
  private collectGroupErrors(group: FormGroup, messages: string[]): void {
    Object.keys(group.controls).forEach((field) => {
      const control = group.get(field);
      if (control?.errors) {
        messages.push(...this.getFieldErrorMessages(field, control.errors));
      }
    });
  }

  /**
   * @param {string} field
   * @param {ValidationErrors} errors
   * @return {string[]}
   */
  private getFieldErrorMessages(
    field: string,
    errors: ValidationErrors
  ): string[] {
    const msgs: string[] = [];
    if (errors['required']) msgs.push(this.errorMsg.requiredMsg);
    if (errors['minlength']) msgs.push(this.errorMsg.minLengthMsg);
    if (errors['pattern']) msgs.push(this.errorMsg.specialCharacterMsg);
    if (errors['min']) {
      msgs.push(
        field === Fields.LEVEL_POINTS
          ? this.errorMsg.minInkLevelValueMsg
          : this.errorMsg.minDiscountValueMsg
      );
    }
    if (errors['max']) {
      msgs.push(
        field === Fields.LEVEL_POINTS
          ? this.errorMsg.maxInkLevelValueMsg
          : this.errorMsg.maxDiscountValueMsg
      );
    }
    return msgs;
  }

  /**
   * Enable the VIP edit for the current region
   * @param {VipLevel} vip
   */
  deleteBusinessLevel(vip: VipLevelSettings) {
    this.dialogService.openDeleteVipModal(vip, () =>
      this.retrieveVipConfigFromCurrentRegion()
    );
  }

  /**
   * Add a new line on the vip settings forms
   */
  addNewLine() {
    if (!this.familyId) return;
    this.dialogService.openAddVipModal(
      { familyId: this.familyId, vipSeriesCount: this.vipLevels.length },
      () => this.retrieveVipConfigFromCurrentRegion()
    );
  }

  /**
   * Reset forms
   */
  resetForm() {
    this.vipLevels.forEach((vip) => {
      (this.formVipCurrent.get(vip.id) as FormGroup).reset();
      (this.formVipNext.get(vip.id) as FormGroup).reset();
    });
    this.vipLevels = [];
  }
}
